import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "@react/shared/ui/IconButton"
import Badge      from "@react/shared/ui/Badge"
import ReserveModal from "./ReserveModal"
import {
  destroyReserve,
  startReserve, fixReserve, acceptReserve, rejectReserve, reopenReserve,
} from "../api/reserves"
import { dateFmt } from "../helpers/format"
import ActionButton from "./ActionButton"

const STATUS_TONE = {
  open:        "warning",
  in_progress: "blue",
  fixed:       "amber",
  accepted:    "success",
  rejected:    "danger",
}

const SEVERITY_DOT = {
  low:    "bg-slate-400",
  medium: "bg-amber-500",
  high:   "bg-rose-500",
}

const StatTile = ({ label, value, tone }) => (
  <div className={`px-4 py-3 rounded-xl border ${tone || "bg-white border-slate-200"}`}>
    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
    <div className="text-[18px] font-semibold tabular-nums text-slate-900">{value}</div>
  </div>
)

const ReservesSection = ({ reserves: initial, policies, acceptance }) => {
  const projectId = initial?.create_url?.match(/\/projects\/(\d+)\/reserves/)?.[1]
  const canManage = !!policies?.can_manage_reserves
  const tasks     = initial?.tasks || []
  const pvPdfUrl  = acceptance?.acceptance?.pdf_url

  const [items,  setItems]  = useState(initial?.items || [])
  const [counts, setCounts] = useState(initial?.counts || { open: 0, in_progress: 0, fixed: 0, accepted: 0, rejected: 0, outstanding: 0, total: 0 })
  const [modalMode, setModalMode] = useState(null)
  const [target,    setTarget]    = useState(null)
  const [filter,    setFilter]    = useState("outstanding")

  const recountAndSet = (next) => {
    const c = next.reduce((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1
      return acc
    }, {})
    setItems(next)
    setCounts({
      total:       next.length,
      open:        c.open        || 0,
      in_progress: c.in_progress || 0,
      fixed:       c.fixed       || 0,
      accepted:    c.accepted    || 0,
      rejected:    c.rejected    || 0,
      outstanding: next.length - (c.accepted || 0),
    })
  }

  const upsert = (record) => {
    const next = items.some(it => it.id === record.id)
      ? items.map(it => (it.id === record.id ? record : it))
      : [record, ...items]
    recountAndSet(next)
  }

  const runTransition = (fn, reserve) => fn(projectId, reserve.id).then(response => {
    if (response?.errors) { flashAlert(response.errors.join(", ")); return }
    upsert(response)
  })

  // Creates the snag-clearing work order (pre-filled, linked to the task); the
  // reserve will move to "cleared" automatically when the work order is closed.
  const createWorkorder = (reserve) => {
    fetch(`/projects/${projectId}/workorders.json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
      },
      credentials: "same-origin",
      body: JSON.stringify({ reserve_id: reserve.id }),
    })
      .then(response => response.json())
      .then(payload => {
        if (payload?.ok && payload.reserve) upsert(payload.reserve)
        else if (payload?.error) flashAlert(payload.error)
      })
  }

  const handleDelete = (reserve) => {
    if (!confirm(I18n.t("projects.reserves.actions.confirm_destroy", { number: reserve.number }))) return
    destroyReserve(projectId, reserve.id).then(response => {
      if (response?.success) recountAndSet(items.filter(it => it.id !== reserve.id))
    })
  }

  const filtered = useMemo(() => {
    if (filter === "all") return items
    if (filter === "outstanding") return items.filter(it => it.status !== "accepted")
    return items.filter(it => it.status === filter)
  }, [items, filter])

  return (
    <section id="section-reserves" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{I18n.t("projects.reserves.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{I18n.t("projects.reserves.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {pvPdfUrl && (
            <a
              href      = {pvPdfUrl}
              target    = "_blank"
              rel       = "noopener noreferrer"
              className = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-[12.5px] font-medium"
              title     = {I18n.t("projects.acceptances.pdf.print")}
            >
              <FontAwesomeIcon icon="file-pdf" /> {I18n.t("projects.acceptances.pdf.print")}
            </a>
          )}
          {canManage && (
            <ActionButton onClick={() => { setTarget(null); setModalMode("new") }}>
              {I18n.t("projects.reserves.actions.new")}
            </ActionButton>
          )}
        </div>
      </header>

      <div className="grid gap-3 grid-cols-2 md:grid-cols-5 mb-4">
        <StatTile label={I18n.t("projects.reserves.summary.total")}        value={counts.total} />
        <StatTile label={I18n.t("projects.reserves.summary.open")}         value={counts.open}        tone="bg-amber-50/60 border-amber-200" />
        <StatTile label={I18n.t("projects.reserves.summary.in_progress")}  value={counts.in_progress} tone="bg-blue-50/60 border-blue-200" />
        <StatTile label={I18n.t("projects.reserves.summary.fixed")}        value={counts.fixed}       tone="bg-amber-50/60 border-amber-200" />
        <StatTile label={I18n.t("projects.reserves.summary.accepted")}     value={counts.accepted}    tone="bg-emerald-50/60 border-emerald-200" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          {[
            { id: "outstanding", label: I18n.t("projects.reserves.filters.outstanding") },
            { id: "all",         label: I18n.t("projects.reserves.filters.all") },
            { id: "open",        label: I18n.t("projects.reserves.statuses.open") },
            { id: "in_progress", label: I18n.t("projects.reserves.statuses.in_progress") },
            { id: "fixed",       label: I18n.t("projects.reserves.statuses.fixed") },
            { id: "accepted",    label: I18n.t("projects.reserves.statuses.accepted") },
            { id: "rejected",    label: I18n.t("projects.reserves.statuses.rejected") },
          ].map(f => (
            <button
              key       = {f.id}
              type      = "button"
              onClick   = {() => setFilter(f.id)}
              className = {`px-3 py-1 rounded-full text-[12px] font-medium ring-1 ring-inset transition-colors ${
                filter === f.id
                  ? "bg-slate-900 text-white ring-slate-900"
                  : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-[90px_minmax(0,2.5fr)_minmax(0,1.2fr)_110px_120px_110px_160px] gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">
          <div>{I18n.t("projects.reserves.table.number")}</div>
          <div>{I18n.t("projects.reserves.table.title")}</div>
          <div>{I18n.t("projects.reserves.table.location_task")}</div>
          <div>{I18n.t("projects.reserves.table.severity")}</div>
          <div>{I18n.t("projects.reserves.table.due_date")}</div>
          <div>{I18n.t("projects.reserves.table.status")}</div>
          <div className="text-right">{I18n.t("projects.reserves.table.actions")}</div>
        </div>

        {filtered.length === 0 ? (
          <div className="px-6 py-10 text-center text-[13px] text-slate-500">
            {filter === "outstanding" && items.length > 0 ? (
              <div className="space-y-2">
                <p>{I18n.t("projects.reserves.empty_outstanding_with_accepted", { count: counts.accepted })}</p>
                <button
                  type      = "button"
                  onClick   = {() => setFilter("all")}
                  className = "inline-flex items-center gap-1.5 text-[12.5px] font-medium text-blue-700 hover:text-blue-900 underline decoration-dotted underline-offset-4"
                >
                  <FontAwesomeIcon icon="eye" className="w-3 h-3" />
                  {I18n.t("projects.reserves.show_all_cta")}
                </button>
              </div>
            ) : (
              I18n.t("projects.reserves.empty")
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(reserve => (
              <div
                key       = {reserve.id}
                className = "grid grid-cols-[90px_minmax(0,2.5fr)_minmax(0,1.2fr)_110px_120px_110px_160px] gap-3 px-4 py-3 items-center hover:bg-slate-50/60 text-[12.5px]"
              >
                <div className="font-mono text-slate-700">{reserve.number}</div>
                <div className="min-w-0">
                  <button
                    type      = "button"
                    onClick   = {() => { setTarget(reserve); setModalMode("edit") }}
                    className = "text-slate-900 font-medium truncate text-left w-full hover:text-blue-700"
                    title     = {reserve.title}
                  >
                    {reserve.title}
                  </button>
                  <span className="inline-flex items-center gap-2 mt-0.5">
                    {reserve.photos_count > 0 && (
                      <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                        <FontAwesomeIcon icon="camera" className="w-3 h-3" />
                        {reserve.photos_count}
                      </span>
                    )}
                    {/* Une réserve du procès-verbal et un désordre signalé pendant
                        la garantie portent le même nom et n'engagent pas la même
                        chose. La liste le dit. */}
                    {reserve.origin && (
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                        reserve.origin === "reception" ? "bg-slate-100 text-slate-500" : "bg-amber-50 text-amber-700"
                      }`}>
                        {I18n.t(`projects.guarantee.origin_${reserve.origin}`)}
                      </span>
                    )}
                  </span>
                </div>
                <div className="min-w-0 text-[11.5px] text-slate-600">
                  {reserve.project_task_title && (
                    <div className="truncate text-blue-700">{reserve.project_task_title}</div>
                  )}
                  {reserve.location && (
                    <div className="truncate text-slate-500">{reserve.location}</div>
                  )}
                </div>
                <div className="inline-flex items-center gap-1.5 text-slate-700">
                  <span className={`w-2 h-2 rounded-full ${SEVERITY_DOT[reserve.severity] || "bg-slate-300"}`} />
                  {reserve.severity_label}
                </div>
                <div className="text-slate-600">{dateFmt(reserve.due_date)}</div>
                <div>
                  <Badge tone={STATUS_TONE[reserve.status] || "neutral"} variant="soft" size="sm">
                    {reserve.status_label}
                  </Badge>
                </div>
                <div className="flex items-center justify-end gap-0.5">
                  {reserve.workorder ? (
                    <a
                      href      = {reserve.workorder.path}
                      target    = "_blank"
                      rel       = "noopener"
                      title     = {`${reserve.workorder.name || I18n.t("projects.reserves.workorder_fallback")} — ${reserve.workorder.status_label}`}
                      className = {`inline-flex items-center justify-center w-8 h-8 rounded hover:bg-slate-100 ${reserve.workorder.status === "Closed" ? "text-emerald-600" : "text-blue-600"}`}
                    >
                      <FontAwesomeIcon icon="clipboard-check" />
                    </a>
                  ) : canManage && ["open", "in_progress", "rejected"].includes(reserve.status) && (
                    <IconButton size="md" title={I18n.t("projects.reserves.actions.create_workorder")} onClick={() => createWorkorder(reserve)}>
                      <FontAwesomeIcon icon="clipboard-list" />
                    </IconButton>
                  )}
                  {canManage && reserve.status === "open" && (
                    <IconButton size="md" title={I18n.t("projects.reserves.actions.start")} onClick={() => runTransition(startReserve, reserve)}>
                      <FontAwesomeIcon icon="play" />
                    </IconButton>
                  )}
                  {canManage && (reserve.status === "open" || reserve.status === "in_progress" || reserve.status === "rejected") && (
                    <IconButton size="md" title={I18n.t("projects.reserves.actions.fix")} onClick={() => runTransition(fixReserve, reserve)}>
                      <FontAwesomeIcon icon="screwdriver-wrench" />
                    </IconButton>
                  )}
                  {canManage && reserve.status === "fixed" && (
                    <IconButton size="md" title={I18n.t("projects.reserves.actions.accept")} onClick={() => runTransition(acceptReserve, reserve)}>
                      <FontAwesomeIcon icon="circle-check" />
                    </IconButton>
                  )}
                  {canManage && reserve.status === "fixed" && (
                    <IconButton size="md" title={I18n.t("projects.reserves.actions.reject")} onClick={() => runTransition(rejectReserve, reserve)}>
                      <FontAwesomeIcon icon="xmark" />
                    </IconButton>
                  )}
                  {canManage && (reserve.status === "accepted" || reserve.status === "fixed") && (
                    <IconButton size="md" title={I18n.t("projects.reserves.actions.reopen")} onClick={() => runTransition(reopenReserve, reserve)}>
                      <FontAwesomeIcon icon="rotate-left" />
                    </IconButton>
                  )}
                  {canManage && (
                    <IconButton size="md" title={I18n.t("projects.reserves.actions.edit")} onClick={() => { setTarget(reserve); setModalMode("edit") }}>
                      <FontAwesomeIcon icon="pen" />
                    </IconButton>
                  )}
                  {canManage && (
                    <IconButton tone="danger" size="md" title={I18n.t("projects.reserves.actions.destroy")} onClick={() => handleDelete(reserve)}>
                      <FontAwesomeIcon icon="trash" />
                    </IconButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ReserveModal
        isOpen     = {modalMode != null}
        mode       = {modalMode}
        projectId  = {projectId}
        reserve    = {target}
        tasks      = {tasks}
        canManage  = {canManage}
        onClose    = {() => { setModalMode(null); setTarget(null) }}
        onSaved    = {upsert}
      />
    </section>
  )
}

export default ReservesSection
